import { useRef, useState, type ReactNode } from 'react';
import Draggable from 'react-draggable';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Tabs,
    Tab,
    Tooltip,
    useMediaQuery,
    Paper,
    type PaperProps,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import 'dayjs/locale/es';
import {
    RiCloseLine,
    RiFullscreenLine,
    RiFullscreenExitLine,
    RiUserLine,
    RiNotificationLine,
    RiLockPasswordLine,
    RiFileList2Line,
    RiMailLine,
} from '@remixicon/react';
import { useAboutMe, useIdDestinatarioByUserLogged } from '@/shared/hooks/useConfiguracion.hook';
import { useUserData } from '@/shared/hooks/useUserData.hook';
import type { PerfilEntidad } from '@/shared/types/interfaces/user.interface';
import { storage } from '@/shared/utils/Const.utils';
import { StorageKeys } from '@/shared/types/enums/Storage.enum';
import type { TipoUsuarioPerfilOpcionEnum } from '@/modules/escritorio/types/enums/TipoUsuario.enum';
import { useIdUsuarioByTipoPersona } from '@/shared/hooks/useOrigenFuenteByTipoPersona.hook';
import PersonalConfig from './Personal/Personal';
import NotificacionesConfig from './Notificaciones/Notificaciones';
import TwoFactorAuthenticator from './TwoFactorAuthenticator/TwoFactorAuthenticator';
import DocumentosPersonales from './DocumentosPersonales/DocumentosPersonales';
import BuzonJudicial from './BuzonJudicial';
import { useConfirmation } from '@/shared/hooks/useConfirmation.hook';
import PersonalForm from './Personal/PersonalForm';
import PersonalSkeleton from './Personal/PersonalSkeleton';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function a11yProps(index: number) {
    return {
        id: `config-tab-${index}`,
        'aria-controls': `config-tabpanel-${index}`,
    };
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`config-tabpanel-${index}`}
            aria-labelledby={`config-tab-${index}`}
            {...other}
            className='pt-4 flex-1 min-h-0 overflow-hidden'
        >
            {value === index && <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>}
        </div>
    );
}

interface ConfiguracionPerfilProps {
    open: boolean;
    onClose: () => void;
}

function DraggablePaperComponent(props: PaperProps) {
    const { isMaximized, nodeRef, ...other } = props as any;

    return (
        <Draggable
            key={isMaximized ? 'maximized' : 'draggable'}
            handle="#draggable-configuracion-perfil-title"
            cancel={'[class*="MuiDialogContent-root"], .no-drag'}
            disabled={isMaximized}
            nodeRef={nodeRef}
        >
            <Paper {...other} ref={nodeRef} />
        </Draggable>
    );
}

const ConfiguracionPerfil = ({
    open,
    onClose
}: ConfiguracionPerfilProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isCompactTabs = useMediaQuery(theme.breakpoints.down('md'));
    const [tabValue, setTabValue] = useState(0);
    const [isMaximized, setIsMaximized] = useState(false);
    const [footerContent, setFooterContent] = useState<ReactNode>(null);
    const dialogNodeRef = useRef<HTMLDivElement>(null);
    const {userData} = useUserData();
    const idUsuario = userData?.id || 0;
    const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
    const opcionTipoUsuario = perfil?.opcion as TipoUsuarioPerfilOpcionEnum;

    let idDestinatario = useIdDestinatarioByUserLogged(opcionTipoUsuario);
    let idUsuarioSesion = useIdUsuarioByTipoPersona();

    const {
        confirmated: openForm,
        confirmation: isOpenForm,
        notConfirmated: closeForm
    } = useConfirmation({initialValue: false})

    const {
        data: dataAboutMe,
        isPending: isPendingAboutMe,
        error: errorAboutMe,
        isFetching: isFetchingAboutMe
    } = useAboutMe(idUsuario);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setFooterContent(null);
    };

    const toggleMaximize = () => {
        setIsMaximized((previous) => !previous);
    };

    const handleCloseDialog = () => {
        setIsMaximized(false);
        onClose();
    };

    const TAB_ICON_SIZE = 18;

    const tabsConfig = [
        {
            index: 0,
            fullLabel: t('configuracionPerfil.personalInfo'),
            compactLabel: t('configuracionPerfil.personalInfoShort'),
            icon: RiUserLine
        },
        {
            index: 1,
            fullLabel: t('configuracionPerfil.notifications'),
            compactLabel: t('configuracionPerfil.notificationsShort'),
            icon: RiNotificationLine
        },
        {
            index: 2,
            fullLabel: t('configuracionPerfil.twoFactorAuthenticator'),
            compactLabel: t('configuracionPerfil.twoFactorAuthenticatorShort'),
            icon: RiLockPasswordLine
        },
        {
            index: 3,
            fullLabel: t('configuracionPerfil.personalDocuments'),
            compactLabel: t('configuracionPerfil.personalDocumentsShort'),
            icon: RiFileList2Line
        },
        {
            index: 4,
            fullLabel: t('configuracionPerfil.judicialMailbox'),
            compactLabel: t('configuracionPerfil.judicialMailboxShort'),
            icon: RiMailLine
        }
    ];

    /* console.log(userData) */
    /* console.log(dataAboutMe) */

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (!['backdropClick', 'escapeKeyDown'].includes(reason)) {
                    handleCloseDialog();
                }
            }}
            maxWidth={isMaximized ? false : 'md'}
            fullWidth={!isMaximized}
            fullScreen={isMaximized}
            PaperComponent={DraggablePaperComponent}
            PaperProps={{
                isMaximized,
                nodeRef: dialogNodeRef,
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: isMaximized ? 0 : '1rem',
                    boxShadow: '0 20px 45px rgba(0, 0, 0, 0.25)',
                    ...(isMaximized && {
                        m: 0,
                        width: '100vw',
                        height: '100vh',
                        maxWidth: '100vw',
                        maxHeight: '100vh',
                    }),
                    ...(!isMaximized && {
                        height: 'min(88vh, 52rem)',
                        maxHeight: '52rem',
                    }),
                },
            } as PaperProps}
            disableEscapeKeyDown
            aria-labelledby="draggable-configuracion-perfil-title"
        >
            {/* Header */}
            <DialogTitle
                id="draggable-configuracion-perfil-title"
                className="flex justify-between items-center pb-4 pt-6 px-6 border-b border-gray-100"
                sx={{
                    cursor: isMaximized ? 'default' : 'move',
                    userSelect: 'none'
                }}
            >
                <div>
                    <h2 className="text-xl font-semibold text-gray-800">
                        {t('configuracionPerfil.title')}
                    </h2>
                </div>
                <Box className="flex items-center gap-1 no-drag">
                    <Tooltip title={isMaximized ? 'Restaurar' : 'Maximizar'} arrow>
                        <IconButton
                            onClick={toggleMaximize}
                            size="small"
                            className="text-gray-500 hover:bg-gray-100 transition-colors"
                        >
                            {isMaximized ? <RiFullscreenExitLine size={18} /> : <RiFullscreenLine size={18} />}
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        onClick={handleCloseDialog}
                        size="small"
                        className="text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                        <RiCloseLine size={20} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                className="px-6 pb-6 pt-4"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'grey.300',
                        minWidth: 0,
                        pb: 0,
                        backgroundColor: 'transparent',
                        overflowY: 'hidden',
                        flexShrink: 0,
                    }}
                >
                    <Tabs 
                        value={tabValue} 
                        onChange={handleChange} 
                        variant={isCompactTabs ? 'scrollable' : 'fullWidth'}
                        scrollButtons={isCompactTabs ? 'auto' : false}
                        allowScrollButtonsMobile
                        aria-label="config tabs"
                        sx={{
                            minHeight: { xs: 46, md: 62 },
                            px: 0,
                            '& .MuiTabs-flexContainer': {
                                gap: { xs: 0.5, md: 0 }
                            },
                            '& .MuiTabs-scroller': {
                                overflowY: 'hidden !important'
                            },
                            '& .MuiTabs-indicator': {
                                display: 'none'
                            },
                            '& .MuiTabs-scrollButtons': {
                                border: '1px solid',
                                borderColor: 'grey.300',
                                borderRadius: '999px',
                                width: 30,
                                height: 30,
                                mx: 0.5,
                                color: 'text.secondary',
                                '&.Mui-disabled': {
                                    opacity: 0.35
                                }
                            }
                        }}
                    >
                        {
                            tabsConfig.map((tab) => (
                                <Tab
                                    key={tab.index}
                                    icon={
                                        <Box
                                            sx={{
                                                width: TAB_ICON_SIZE,
                                                height: TAB_ICON_SIZE,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <tab.icon size={TAB_ICON_SIZE} />
                                        </Box>
                                    }
                                    iconPosition='start'
                                    label={
                                        <Tooltip
                                            title={tab.fullLabel}
                                            enterDelay={500}
                                            disableInteractive
                                        >
                                            <span>{isCompactTabs ? tab.compactLabel : tab.fullLabel}</span>
                                        </Tooltip>
                                    }
                                    aria-label={tab.fullLabel}
                                    {...a11yProps(tab.index)}
                                    sx={{
                                        minHeight: { xs: 44, md: 60 },
                                        minWidth: isCompactTabs ? 92 : 0,
                                        px: { xs: 1.1, sm: 1.6 },
                                        py: 1,
                                        mr: isCompactTabs ? 0.25 : 0,
                                        flex: isCompactTabs ? '0 0 auto' : 1,
                                        alignItems: 'center',
                                        textTransform: 'none',
                                        fontSize: { xs: '0.76rem', sm: '0.84rem' },
                                        fontWeight: 500,
                                        lineHeight: 1.25,
                                        whiteSpace: isCompactTabs ? 'nowrap' : 'normal',
                                        borderRadius: '0.6rem 0.6rem 0 0',
                                        border: '1px solid',
                                        borderColor: 'transparent',
                                        color: 'text.secondary',
                                        transition: 'all 0.2s ease-in-out',
                                        '& .MuiTab-iconWrapper': {
                                            width: TAB_ICON_SIZE,
                                            height: TAB_ICON_SIZE,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '0.45rem',
                                            marginBottom: 0
                                        },
                                        '&:hover': {
                                            color: 'text.primary',
                                            backgroundColor: 'grey.50'
                                        },
                                        '&.Mui-selected': {
                                            color: 'text.primary',
                                            backgroundColor: 'common.white',
                                            borderColor: 'grey.300',
                                            borderBottomColor: 'common.white',
                                            marginBottom: '-1px',
                                            fontWeight: 600
                                        },
                                        '&.Mui-focusVisible': {
                                            outline: '2px solid',
                                            outlineColor: 'primary.main',
                                            outlineOffset: 1
                                        }
                                    }}
                                />
                            ))
                        }
                    </Tabs>
                </Box>
                <CustomTabPanel value={tabValue} index={0}>
                    {
                        isPendingAboutMe 
                        ? <PersonalSkeleton />
                        : !isOpenForm 
                        ? <PersonalConfig openForm={openForm} telefono={dataAboutMe?.Telefono || ''} correo={dataAboutMe?.Email || ''} />
                        : <PersonalForm
                            closeForm={closeForm}
                            onRegisterFooter={setFooterContent}
                            datosUsuario={{
                                Celular: '',
                                Correo: dataAboutMe?.Email!, //|| userData?.email!,
                                Foto: dataAboutMe?.UrlFotoUsuario!, //|| userData?.image!,
                                IdUsuario: dataAboutMe?.IdUsuario!, //|| userData?.id!,
                                NombreFoto: userData?.fullName!,
                                Telefono: dataAboutMe?.Telefono! //|| userData?.telefono!
                            }}
                        />
                    }
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={1}>
                    <NotificacionesConfig idUsuario={idUsuario} idDestinatario={idDestinatario} idUsuarioSesion={idUsuarioSesion} onClose={handleCloseDialog} onRegisterFooter={setFooterContent}/>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={2}>
                    <TwoFactorAuthenticator isActiveTwoFA={userData?.esActivo2FA!} onClose={handleCloseDialog} onRegisterFooter={setFooterContent}/>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={3}>
                    <DocumentosPersonales />
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={4}>
                    <BuzonJudicial />
                </CustomTabPanel>
            </DialogContent>
            {footerContent && (
                <DialogActions sx={{ px: 3, pb: 2, pt: 0, borderTop: '1px solid', borderColor: 'grey.200' }}>
                    {footerContent}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ConfiguracionPerfil;